// Busca semântica inteligente - reutiliza lib/search/smartSearch
import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { smartSearch, SmartSearchResult, SEARCH_SUGGESTIONS } from '@/lib/search/smartSearch';
import { cn } from '@/lib/utils';

interface SmartSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SmartSearch = ({ isOpen, onClose }: SmartSearchProps) => {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  
  const searchResults = useMemo(() => {
    return smartSearch(query);
  }, [query]);
  
  const handleSelect = useCallback((result: SmartSearchResult) => {
    navigate(result.url);
    onClose();
    setQuery('');
  }, [navigate, onClose]);

  const handleSearchSubmit = useCallback(() => {
    if (query.trim().length >= 2) {
      navigate(`/busca?q=${encodeURIComponent(query.trim())}`);
      onClose();
      setQuery('');
    }
  }, [query, navigate, onClose]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchSubmit();
    }
  }, [handleSearchSubmit]);
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Buscar produtos e rotinas</DialogTitle>
        </DialogHeader>
        
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ex: coceira, pele rachada, pós-quimio, sem perfume..."
            className="pl-10 pr-10"
            autoFocus
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        {searchResults.length > 0 && (
          <div className="mt-4 space-y-2 max-h-80 overflow-y-auto">
            {searchResults.map((result, index) => (
              <button
                key={`${result.type}-${result.url}-${index}`}
                onClick={() => handleSelect(result)}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted text-left transition-colors"
              >
                <div className={cn(
                  "flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-xs font-medium overflow-hidden",
                  result.type === 'product' ? "bg-primary/10 text-primary" : "bg-secondary text-secondary-foreground"
                )}>
                  {result.image ? (
                    <img src={result.image} alt="" className="w-full h-full object-contain" loading="lazy" />
                  ) : (
                    result.type === 'product' ? '📦' : '✨'
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">
                    {result.title}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {result.type === 'product' ? 'Produto' : 'Rotina recomendada'}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
              </button>
            ))}
          </div>
        )}
        
        {query.length >= 2 && searchResults.length === 0 && (
          <div className="mt-4 text-center py-6 text-muted-foreground">
            <p>Nenhum resultado encontrado</p>
            <Button variant="link" asChild className="mt-2">
              <a href={`/busca?q=${encodeURIComponent(query)}`}>Ver página de busca</a>
            </Button>
          </div>
        )}
        
        {query.length < 2 && (
          <div className="mt-4 text-xs text-muted-foreground">
            <p className="font-medium mb-2">Sugestões de busca:</p>
            <div className="flex flex-wrap gap-2">
              {SEARCH_SUGGESTIONS.map(term => (
                <button
                  key={term}
                  onClick={() => setQuery(term)}
                  className="px-2 py-1 bg-muted rounded text-foreground hover:bg-muted/80"
                >
                  {term}
                </button>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
